"use client";

import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import api from "../../../servicos/api";
import { formatarValor } from "../../../utils/formatarValor";

// Estilos personalizados
const useStyles = makeStyles({
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardsContainer: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    justifyContent: "center",
    alignItems: "stretch",
    "@media (max-width: 768px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  card: {
    flex: "1",
    maxWidth: "250px",
    minHeight: "90px",
    padding: "14px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    borderRadius: "12px",
    background: "#F3F7FF",
    alignItems: "center",
    justifyContent: "center",
    "@media (max-width: 768px)": {
      flex: "none",
      maxWidth: "100%",
      width: "100%",
    },
  },
  cardLabel: {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "120%",
    textAlign: "center",
    color: "#727986",
  },
  cardValue: {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 700,
    fontSize: "22px",
    lineHeight: "120%",
    textAlign: "center",
    color: "#3A414E",
    wordWrap: "break-word",
  },
  instrucoesSection: {
    marginTop: "16px",
    marginBottom: "16px",
    padding: "0 8px",
  },
  instrucoesTitle: {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "120%",
    color: "#262F40",
    marginBottom: "8px",
  },
  instrucoesContent: {
    fontFamily: "'Inter', sans-serif",
    fontSize: "14px",
    lineHeight: "140%",
    color: "#727986",
    whiteSpace: "pre-line",
    padding: "10px 12px",
    borderRadius: "8px",
    textAlign: "justify",
  },
  detailsSection: {
    padding: "0 8px",
  },
  detailItem: {
    marginBottom: "20px",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  detailLabel: {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "120%",
    color: "#2A303C",
    marginBottom: "6px",
  },
  detailContent: {
    fontFamily: "'Inter', sans-serif",
    fontSize: "14px",
    lineHeight: "140%",
    color: "#727986",
    whiteSpace: "pre-line",
    padding: "10px 12px",
    borderRadius: "8px",
    textAlign: "justify",
  },
});

// Cache global para os detalhes dos exames - evita buscar novamente
const cacheDetalhes = {};

function DetalheExame({ id, preco }) {
  const [detalhe, setDetalhe] = useState(cacheDetalhes[id] || []);
  const [loading, setLoading] = useState(!cacheDetalhes[id]);
  const classes = useStyles();

  // Solicita a consulta no banco de dados filtrando pelo id
  useEffect(() => {
    // Se já tem no cache, não busca novamente
    if (cacheDetalhes[id]) {
      setDetalhe(cacheDetalhes[id]);
      setLoading(false);
      return;
    }

    async function carregarDetalhe() {
      setLoading(true);
      try {
        const response = await api.get(`/api/detalheExame/` + id);
        // Salva no cache
        cacheDetalhes[id] = response.data;
        setDetalhe(response.data);
      } catch (error) {
        console.error('Erro ao carregar detalhes:', error);
      } finally {
        setLoading(false);
      }
    }
    carregarDetalhe();
  }, [id]);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Carregando...</div>;
  }

  if (detalhe.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Nenhum detalhe encontrado</div>;
  }

  const det = detalhe[0];

  return (
    <div id="detalheExame" className={classes.container}>
      {/* Cards com Preço, Material e Prazo de Entrega */}
      <Box className={classes.cardsContainer}>
        <Box className={classes.card}>
          <Typography className={classes.cardLabel}>Preço</Typography>
          <Typography className={classes.cardValue}>
            {preco !== undefined && preco !== null ? formatarValor(parseFloat(preco) || 0) : 'Consultar preço'}
          </Typography>
        </Box>
        <Box className={classes.card}>
          <Typography className={classes.cardLabel}>Material</Typography>
          <Typography className={classes.cardValue}>
            {det.material || "-"}
          </Typography>
        </Box>
        <Box className={classes.card}>
          <Typography className={classes.cardLabel}>Prazo de Entrega</Typography>
          <Typography className={classes.cardValue}>
            {det.entrega || "-"}
          </Typography>
        </Box>
      </Box>

      {/* Instruções de preparo e coleta */}
      {/* <Box className={classes.instrucoesSection}>
        <Typography className={classes.instrucoesTitle}>
          Instruções de preparo e coleta
        </Typography>
        <Typography className={classes.instrucoesContent}>
          {det.instrucoes || "-"}
        </Typography>
      </Box> */}

      {/* Informações detalhadas */}
      <Box className={classes.detailsSection}>
        <Box className={classes.detailItem}>
          <Typography className={classes.detailLabel}>Descrição</Typography>
          <Typography className={classes.detailContent}>{det.nome || "-"}</Typography>
        </Box>

        <Box className={classes.detailItem}>
          <Typography className={classes.detailLabel}>Sinônimos</Typography>
          <Typography className={classes.detailContent}>{det.sinonimoExame || "-"}</Typography>
        </Box>

        <Box className={classes.detailItem}>
          <Typography className={classes.detailLabel}>Informações Gerais</Typography>
          <Typography className={classes.detailContent}>{det.informacoesGerais || "-"}</Typography>
        </Box>
      </Box>
    </div>
  );
}

// Otimização: React.memo para evitar re-renders desnecessários
export default React.memo(DetalheExame);
